import React, { useState, useEffect } from 'react';
import { apiClient } from '../api';
import { Monitor, RefreshCw, Clock, CheckCircle, AlertCircle, Server, Trash2, Plus, Copy, Check, X, Loader2 } from 'lucide-react';

const AgentRegistry = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [registerForm, setRegisterForm] = useState({ hostname: '', os_type: 'Linux', os_info: '', ip_address: '' });
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerResult, setRegisterResult] = useState(null);
  const [registerError, setRegisterError] = useState('');
  const [copied, setCopied] = useState(false);

  const handleRegisterChange = (field, value) => {
    setRegisterForm(prev => ({ ...prev, [field]: value }));
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setRegisterLoading(true);
    setRegisterError('');
    setRegisterResult(null);
    try {
      const res = await apiClient.post('/api/register', registerForm);
      setRegisterResult(res.data);
    } catch (err) {
      setRegisterError(err.response?.data?.error || err.message);
    } finally {
      setRegisterLoading(false);
    }
  };

  const handleCopyKey = async () => {
    if (registerResult?.api_key) {
      try {
        await navigator.clipboard.writeText(registerResult.api_key);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {}
    }
  };

  const closeRegisterModal = () => {
    setShowRegisterModal(false);
    setRegisterResult(null);
    setRegisterError('');
    setRegisterForm({ hostname: '', os_type: 'Linux', os_info: '', ip_address: '' });
    setCopied(false);
    fetchAgents();
  };

  const fetchAgents = async () => {
    try {
      setLoading(true);
      const res = await apiClient.get('/api/servers');
      const servers = res.data.servers || [];
      const mappedAgents = servers.map(s => ({
        agent_id: s.id,
        hostname: s.hostname,
        os_type: s.os_type,
        os_version: s.os_info || '',
        last_report: s.last_seen,
        total_reports: 0,
        status: s.last_seen ? 'active' : 'registered',
      }));
      setAgents(mappedAgents);
    } catch {
      setAgents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAgents(); const interval = setInterval(fetchAgents, 30000); return () => clearInterval(interval); }, []);

  const deleteAgent = async (agentId) => {
    if (!confirm(`Delete agent ${agentId}?`)) return;
    try {
      await apiClient.delete(`/api/servers/${agentId}`);
      fetchAgents();
    } catch {
      alert('Failed to delete agent');
    }
  };

  const getStatusIcon = (agent) => {
    if (agent.status === 'active' && agent.last_report) {
      const lastReport = new Date(agent.last_report);
      const minutesAgo = (Date.now() - lastReport.getTime()) / 60000;
      if (minutesAgo < 15) return <CheckCircle className="w-4 h-4 text-green-400" />;
      if (minutesAgo < 60) return <Clock className="w-4 h-4 text-yellow-400" />;
    }
    return <AlertCircle className="w-4 h-4 text-gray-500" />;
  };

  const getStatusText = (agent) => {
    if (!agent.last_report) return 'Registered';
    const lastReport = new Date(agent.last_report);
    const minutesAgo = Math.round((Date.now() - lastReport.getTime()) / 60000);
    if (minutesAgo < 15) return 'Active';
    if (minutesAgo < 60) return `${minutesAgo}m ago`;
    return `${Math.round(minutesAgo / 60)}h ago`;
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">Agent Registry</h1>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowRegisterModal(true)} className="flex items-center gap-2 px-3 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg text-sm text-white transition-colors">
            <Plus className="w-4 h-4" /> Register Agent
          </button>
          <button onClick={fetchAgents} className="flex items-center gap-2 px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm text-gray-300 transition-colors">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <div className="text-3xl font-bold text-white">{agents.length}</div>
          <div className="text-sm text-gray-400">Total Agents</div>
        </div>
        <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4">
          <div className="text-3xl font-bold text-green-400">
            {agents.filter(a => a.last_report && (Date.now() - new Date(a.last_report).getTime()) / 60000 < 15).length}
          </div>
          <div className="text-sm text-green-400/70">Active (last 15m)</div>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <div className="text-3xl font-bold text-white">
            {agents.filter(a => a.os_type).length}
          </div>
          <div className="text-sm text-gray-400">Endpoints Discovered</div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading agents...</div>
      ) : agents.length === 0 ? (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-12 text-center">
          <Monitor className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-400 mb-2">No Agents Registered</h3>
          <p className="text-sm text-gray-500 mb-4">
            Deploy the Sentinel agent on endpoints to begin passive asset discovery.
          </p>
          <div className="bg-gray-950 border border-gray-800 rounded-lg p-4 text-left max-w-lg mx-auto">
            <p className="text-xs text-gray-400 mb-2">Quick start:</p>
            <code className="text-xs text-gray-500 block mb-1">
              # 1. Register this machine (replace TENANT_KEY with your org key):
            </code>
            <code className="text-xs text-cyan-400 block mb-2">
              python agent.py --register --tenant-key YOUR_TENANT_API_KEY
            </code>
            <code className="text-xs text-gray-500 block mb-1">
              # 2. Copy the returned API_KEY into .env and run:
            </code>
            <code className="text-xs text-cyan-400 block">
              python agent.py
            </code>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {agents.map((agent, i) => (
            <div key={i} className="bg-gray-900 border border-gray-800 rounded-xl p-4 hover:bg-gray-900/80 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-800 rounded-lg">
                    <Monitor className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-white">{agent.agent_id}</span>
                      {getStatusIcon(agent)}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {agent.hostname && <span>{agent.hostname} </span>}
                      {agent.os_type && <span className="text-gray-400">{agent.os_type} {agent.os_version}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-right">
                    <div className="text-sm text-gray-300">{getStatusText(agent)}</div>
                    <div className="text-xs text-gray-500">
                      {agent.last_report ? 'Reporting' : 'No reports yet'}
                    </div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteAgent(agent.agent_id); }}
                    className="p-1.5 text-gray-500 hover:text-red-400 transition-colors ml-2"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showRegisterModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={closeRegisterModal}>
          <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
            {!registerResult ? (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-white">Register New Agent</h2>
                  <button onClick={closeRegisterModal} className="text-gray-500 hover:text-white transition-colors">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <form onSubmit={handleRegisterSubmit}>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Hostname *</label>
                      <input type="text" value={registerForm.hostname} onChange={e => handleRegisterChange('hostname', e.target.value)} placeholder="e.g. web-server-01" className="w-full px-3 py-2 bg-gray-950 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:border-cyan-500 transition-colors" required />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">OS Type *</label>
                      <select value={registerForm.os_type} onChange={e => handleRegisterChange('os_type', e.target.value)} className="w-full px-3 py-2 bg-gray-950 border border-gray-700 rounded-lg text-sm text-gray-300 focus:outline-none focus:border-cyan-500 transition-colors">
                        <option value="Linux">Linux</option>
                        <option value="Windows">Windows</option>
                        <option value="macOS">macOS</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">OS Info</label>
                      <input type="text" value={registerForm.os_info} onChange={e => handleRegisterChange('os_info', e.target.value)} placeholder="e.g. Ubuntu 22.04" className="w-full px-3 py-2 bg-gray-950 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:border-cyan-500 transition-colors" />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">IP Address</label>
                      <input type="text" value={registerForm.ip_address} onChange={e => handleRegisterChange('ip_address', e.target.value)} placeholder="e.g. 192.168.1.100" className="w-full px-3 py-2 bg-gray-950 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:border-cyan-500 transition-colors" />
                    </div>
                  </div>
                  {registerError && (
                    <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-sm text-red-400">{registerError}</div>
                  )}
                  <div className="flex justify-end gap-3 mt-6">
                    <button type="button" onClick={closeRegisterModal} className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors">Cancel</button>
                    <button type="submit" disabled={registerLoading} className="flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-500 disabled:bg-cyan-600/50 rounded-lg text-sm text-white transition-colors">
                      {registerLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                      {registerLoading ? 'Registering...' : 'Register'}
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-green-400">Agent Registered!</h2>
                  <button onClick={closeRegisterModal} className="text-gray-500 hover:text-white transition-colors">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="space-y-4">
                  <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                    <p className="text-sm text-green-400">Server <span className="font-semibold">{registerResult.hostname}</span> registered successfully.</p>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-1">API Key</label>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 p-3 bg-gray-950 border border-gray-700 rounded-lg text-xs text-cyan-400 font-mono break-all">{registerResult.api_key}</code>
                      <button onClick={handleCopyKey} className="p-3 bg-gray-800 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-white transition-colors">
                        {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="p-3 bg-gray-950 border border-gray-700 rounded-lg">
                    <p className="text-xs text-gray-400 mb-1">On the target machine, add to .env:</p>
                    <code className="text-xs text-cyan-400">API_KEY={registerResult.api_key}</code>
                  </div>
                </div>
                <div className="flex justify-end mt-6">
                  <button onClick={closeRegisterModal} className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg text-sm text-white transition-colors">Done</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentRegistry;
